/**
 * Data-owning document summary page and note/document deep-link resolver.
 */

import { useAuth } from "@risalabs_frontend_org/auth-wrapper";
import { useEffect, useState } from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SpinningLoader } from "risa-oasis-ui_v2";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import {
  mergeDetailIntoPatient,
  mergeSummaryIntoPatient,
  rowToPatient,
  toBackendDocType,
  toFrontendDocType,
} from "../api/transformers";
import { DocumentSummaryView } from "../views/DocumentSummaryView";
import type { DocumentType, Patient } from "../types";

/** Yesterday's date as YYYY-MM-DD (UTC). */
function yesterdayIso(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split("T")[0];
}

/** Router state passed from the Notification View when a row is opened. */
interface SummaryRouteState {
  patient?: Patient;
  documentType?: DocumentType;
  date?: string;
  /** Exact notifications URL that opened this summary. */
  returnTo?: string;
}

/**
 * Container for the Document Summary View. Resolves the target patient (from
 * router state, or by refetching the day's notifications on a deep link),
 * fetches the per-type structured summary, resolves a signed PDF URL for
 * "View Source", and renders the summary. CT admins return to Notes;
 * superadmins retain their Notification View return destination.
 *
 * @returns The data-connected Document Summary View
 */
export function DocumentSummaryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { mrn = "" } = useParams();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = (location.state as SummaryRouteState) || {};

  const requestedDocumentType = searchParams.get("documentType");
  const documentType: DocumentType = requestedDocumentType
    ? toFrontendDocType(requestedDocumentType)
    : state.documentType || "Radiology";
  // URL-first so it survives a setSearchParams that drops router state (e.g. opening
  // Patient Notes); otherwise `date` would revert to yesterday and re-fire the load.
  const date = searchParams.get("date") || state.date || yesterdayIso();
  const requestedDocumentId = searchParams.get("documentId") || undefined;
  const targetNoteId = searchParams.get("noteId") || undefined;
  const notesOpen = searchParams.get("notes") === "open";
  const sourceUnavailable = searchParams.get("sourceUnavailable") === "true";

  const [patient, setPatient] = useState<Patient | null>(state.patient || null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    async function load() {
      let basePatient = state.patient || null;

      // Deep link (no router state): rebuild the base patient from the day's roll-up.
      if (!basePatient) {
        try {
          // Only this MRN's row is needed. Narrow server-side (query matches name/MRN) instead of
          // downloading the whole day. `query` is a case-insensitive SUBSTRING match, so a short
          // MRN can match >1 row; hitsPerPage=0 returns every match on one page so the exact
          // client-side find still resolves the deep link without truncating collisions.
          const res = await clinicalTrialsService.getNotifications(
            { date, query: mrn, hitsPerPage: 0 },
            controller.signal,
          );
          const row = res.hits.find((p) => p.mrn === mrn);
          basePatient = row ? rowToPatient(row) : null;
        } catch {
          basePatient = null;
        }
      }

      // Enrich the header (identity, details grid, diagnosis section) from the
      // full patient record. Best-effort: the row-derived base still renders if
      // the patient hasn't been enriched yet.
      try {
        const detail = await clinicalTrialsService.getPatientDetail(
          mrn,
          controller.signal,
        );
        const detailBase =
          basePatient ||
          rowToPatient({
            mrn,
            patient_name: detail.patient_name,
            document_types: [toBackendDocType(documentType)],
            document_ids: requestedDocumentId ? [requestedDocumentId] : [],
            trials: [],
            is_watchlisted: false,
          });
        basePatient = mergeDetailIntoPatient(detailBase, detail);
      } catch {
        /* No enriched record yet — the header falls back to row-level fields. */
      }

      // Notes inbox links identify the exact originating document. Preserve that
      // identity even when patient detail or a daily roll-up supplied another row.
      if (basePatient && requestedDocumentId) {
        basePatient = {
          ...basePatient,
          documentId: requestedDocumentId,
          documentTypes: Array.from(
            new Set([...basePatient.documentTypes, documentType]),
          ),
        };
      }

      // Enrich with the per-type structured summary.
      try {
        const summary = await clinicalTrialsService.getDocumentSummary(
          mrn,
          toBackendDocType(documentType),
          date,
          controller.signal,
        );
        if (basePatient) {
          basePatient = mergeSummaryIntoPatient(basePatient, summary);
        }
      } catch {
        /* No summary yet — the view renders its empty/"coming soon" state. */
      }

      // Resolve a signed source-PDF URL for "View Source". For an expanded per-document
      // row, resolve THAT exact document by id (so two same-date reports open different
      // PDFs); otherwise fall back to the latest stored document of the type by (mrn, type).
      try {
        const pdf = basePatient?.documentId
          ? await clinicalTrialsService.getDocumentPdfUrl(basePatient.documentId, controller.signal)
          : await clinicalTrialsService.getSourcePdfUrl(
              mrn,
              toBackendDocType(documentType),
              controller.signal,
            );
        if (basePatient) {
          basePatient = {
            ...basePatient,
            sourceDocuments: {
              ...(basePatient.sourceDocuments || {}),
              [documentType]: pdf.url,
            },
          };
        }
      } catch {
        /* PDF URL is best-effort; the view shows a fallback alert. */
      }

      if (!controller.signal.aborted) {
        setPatient(basePatient);
        setIsLoading(false);
      }
    }

    load();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mrn, documentType, date, requestedDocumentId]);

  // Show the wait screen for the FULL load, not just when there's no patient. On a
  // row-click we receive a stale row-level `state.patient` (wrong diagnosis band/codes
  // until the detail + summary resolve ~5-10s later); rendering it would flash corrupt
  // data. Gate the whole view on isLoading so the user only ever sees a loader → real data.
  if (isLoading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ backgroundColor: "var(--color-page-bg)" }}
        role="status"
        aria-live="polite"
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1rem",
          }}
        >
          <SpinningLoader />
          <span
            style={{
              color: "var(--color-text-secondary)",
              fontSize: "var(--font-size-sm)",
            }}
          >
            Loading summary…
          </span>
        </div>
      </div>
    );
  }

  return (
    <DocumentSummaryView
      patient={patient}
      documentType={documentType}
      isLoading={isLoading}
      notesOpen={notesOpen}
      targetNoteId={targetNoteId}
      sourceUnavailable={sourceUnavailable}
      backLabel={user?.isSuperAdmin ? undefined : "Back to Notes"}
      onNotesOpenChange={(open) =>
        setSearchParams(
          (previous) => {
            const next = new URLSearchParams(previous);
            // setSearchParams drops router state, so persist the active tab + date into
            // the URL. Without this, documentType/date revert to their defaults
            // (Radiology / yesterday), the load effect re-fires, and the notes toggle
            // bounces the user to the Radiology tab with a loading flash.
            next.set("documentType", toBackendDocType(documentType));
            next.set("date", date);
            if (open) next.set("notes", "open");
            else {
              next.delete("notes");
              next.delete("noteId");
            }
            return next;
          },
          { replace: true },
        )
      }
      onBack={() =>
        navigate(
          user?.isSuperAdmin
            ? state.returnTo ||
                `/notifications?date=${encodeURIComponent(date)}&tab=${encodeURIComponent(documentType)}`
            : "/notes",
        )
      }
      onPatientUpdate={setPatient}
    />
  );
}
